import { isValidElement } from 'react'
import { matchRoutes, Navigate, type RouteObject } from 'react-router-dom'
import { RoleGuard } from '@/components/guards/RoleGuard'
import { SuperAdminGuard } from '@/features/ferpa/components/SuperAdminGuard'
import type { UserRole } from '@/stores/authStore'

/**
 * Which URLs are real pages for the signed-in user, so "Back to …" never points at a
 * URL prefix with no page (a 404) or at a page the role cannot open (Unauthorized).
 *
 * The route table is registered by the app rather than imported: the table imports the
 * dashboard layout, which uses this module.
 */
let pageRoutes: RouteObject[] | null = null

export function registerPageRoutes(routes: RouteObject[] | null) {
  pageRoutes = routes
}

export type RoleAccess = { roles: UserRole[]; isSuperAdmin: boolean }

function allowedRolesOf(route: RouteObject): UserRole[] | null {
  const element = route.element
  if (isValidElement(element) && element.type === RoleGuard) {
    return (element.props as { allowedRoles: UserRole[] }).allowedRoles
  }
  return null
}

/** Static `<Navigate to='/x' />` targets; dynamic redirect components return null. */
function staticRedirectOf(route: RouteObject): string | null {
  const element = route.element
  if (isValidElement(element) && element.type === Navigate) {
    const to = (element.props as { to: unknown }).to
    return typeof to === 'string' && to.startsWith('/') ? to : null
  }
  return null
}

/**
 * True when `path` opens a page the user may see. Without a registered table every
 * path counts as a page (the old behaviour), which keeps isolated component tests simple.
 */
export function isOpenablePage(path: string, access: RoleAccess, depth = 0): boolean {
  if (!pageRoutes) return true
  if (depth > 3) return false
  const matches = matchRoutes(pageRoutes, path)
  if (!matches) return false
  const leaf = matches[matches.length - 1]?.route
  if (!leaf || leaf.path === '*') return false
  for (const { route } of matches) {
    if (isValidElement(route.element) && route.element.type === SuperAdminGuard) {
      if (!access.isSuperAdmin) return false
      continue
    }
    const allowed = allowedRolesOf(route)
    if (allowed && !access.isSuperAdmin && !allowed.some(role => access.roles.includes(role))) {
      return false
    }
  }
  const redirect = staticRedirectOf(leaf)
  return redirect ? isOpenablePage(redirect.split('?')[0] ?? redirect, access, depth + 1) : true
}
